import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AgGridReact } from 'ag-grid-react';
import { useRecoilValue } from 'recoil';

import { unMatchClaim } from 'api/ERA';

import { useEraContext } from 'lib/context/EraContext/EraContext';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import { currentPractice } from 'components/practice/practiceState';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

import { formatMatchedClaims } from './lib/formatMatchedClaims';
import {
  DEFAULT_COLUMN_DEFS_BOLD,
  MATCHED_CLAIMS_COLUMN_DEFS,
  getRowClassRules
} from './lib/helpers';

const MatchedClaims = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const { era } = useEraContext();
  const { isOpen, openModal, closeModal } = useModal();

  const onCellMouseOver = (event) => {
    if (event?.data?.claimId === hoveredId) return;
    setHoveredId(event.data?.claimId);
    gridRef.current?.api?.refreshCells({ force: true });
  };

  const onCellMouseOut = (event) => {
    if (event?.data?.claimId === hoveredId) return;
    setHoveredId(null);
    gridRef.current?.api?.refreshCells({ force: true });
  };

  const currPractice = useRecoilValue(currentPractice);
  const timezone = currPractice?.timezone;
  const rowData = useMemo(
    () => formatMatchedClaims({ claims: era?.claims, refId: era?.id, refType: era?.type }),
    [era?.claims]
  );

  const mutateUnMatchClaim = useCustomMutation({
    invalidateQueryKey: ['eras', 'eobs', 'eob', 'era', 'claims'],
    successMessage: 'Claim was unposted successfully!',
    errorMessage: 'Failed to unpost the claim.',
    mutationFn: ({ claimId }) =>
      unMatchClaim(navigate, { refId: era?.id, refType: era?.type, claimId }),
    onSuccess: () => {
      setClaimId(null);
      closeModal();
    },
    onError: () => {
      setClaimId(null);
      closeModal();
    }
  });

  const handleUnPostClaim = (claimId) => {
    setClaimId(claimId);
    openModal();
  };

  const columnDefs = useMemo(
    () => MATCHED_CLAIMS_COLUMN_DEFS(timezone, era?.created_at, handleUnPostClaim),
    [timezone]
  );

  return (
    <div className="ag-theme-quartz shadow-none">
      <AgGridReact
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COLUMN_DEFS_BOLD}
        rowData={rowData}
        domLayout="autoHeight"
        onCellMouseOver={onCellMouseOver}
        onCellMouseOut={onCellMouseOut}
        rowClassRules={getRowClassRules('', hoveredId)}
        suppressRowHoverHighlight
      />

      <Confirm
        isOpen={isOpen}
        handleClose={closeModal}
        handleContinue={() => mutateUnMatchClaim.mutate({ claimId })}
        title="Unpost Claim"
        icon="send-backwards-mono"
        message={
          <>
            <p>Are you sure you want to unpost this claim?</p>
            <p>The benefits applied to this claim will also be unallocated.</p>
            <p className="text-danger-500">This action cannot be reverted.</p>
          </>
        }
        primaryBtnTxt="Unpost"
        variant="danger"
      />
    </div>
  );
};

export default MatchedClaims;
